import React, { useEffect } from "react";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { CustomTooltip } from "../../consts.js";
import { useNavigate } from "react-router-dom";
import Utils from "../../utils.js";
import FeatherIcon from "feather-icons-react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CircularProgress from "@mui/material/CircularProgress";
import SettingHeader from "../admin/setting-header.jsx";
import AddTracker from "./add-tracker.jsx";
import AddPhrase from "./add-phrases.jsx";
import { Box, Grid, IconButton, Paper, Stack, TableContainer, TableFooter } from "@mui/material";
import DeleteConfirmButton from "./delete-confirm.jsx";
import { useSnackbar } from "../../components/toast/index.jsx";
import { PHRASE_ADDED_FAILED, PHRASE_ADDED_SUCCESS } from "../../helpers/messages.js";
import { useThemeMode } from "../../components/theme/index.jsx";

function TrackerAdmin() {
    const navigate = useNavigate();
    const showSnackbar = useSnackbar();
    const [data, setData] = React.useState([]);
    const [dataToEdit, setDataToEdit] = React.useState([]);
    const [companyDT, setCompanyDT] = React.useState({});
    const [newCampDiag, setNewCampDiag] = React.useState(false);
    const [selectedTab] = React.useState("Insight admin");
    const [dataLoaded, setDataLoaded] = React.useState(false);
    const [spinner, setSpinner] = React.useState(false);
    const [expanded, setExpanded] = React.useState(false);
    const scrollContainer = React.useRef(null);
    const { colours } = useThemeMode();
    const [changeIndex, setChangeIndex] = React.useState(null);

    const scrollToElement = () => {
        setTimeout(() => {
            const expandedTag = document.querySelector(`#panel${expanded + 1}a-header`);
            if (expandedTag) {
                if ("scrollBehavior" in document.documentElement.style) {
                    expandedTag.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                        inline: "nearest"
                    });
                } else {
                    const elementPosition = expandedTag?.getBoundingClientRect().top;

                    // Fallback for older browsers like IE11
                    scrollContainer?.current?.scrollTo({
                        top: elementPosition - 100, // 100px padding from the top
                        behavior: "smooth"
                    });
                }
            }
        }, 150);
    }

    async function getCompanyData() {
        Utils.sendProgressBar(true);
        setSpinner(true);

        const userGroups = await Utils.getUserGroupFromCognito();

        // Check if user groups include "admin"
        if (!userGroups.includes("admin")) {
            navigate("/", { replace: true })
        }

        let companies = await Utils.genericGet("getCompany", "admin");
        companies = companies.data.getCompany;
        setCompanyDT(companies);
        companies.trackers = JSON.parse(companies.trackers);
        setDataToEdit(
            companies.trackers.map((z) => {
                return { phrases: z.phrases.map((a) => false) };
            })
        );
        setData(companies.trackers);
        setSpinner(false);
        if (!isNaN(expanded)) {
            scrollToElement();
        }
    }

    React.useEffect(() => {
        if (!dataLoaded) {
            const fetchData = async () => {
                await getCompanyData(); // Your async data fetching function
                setDataLoaded(true); // Mark data as loaded
            };
            fetchData();
        }

        window.scrollTo(0, 0); // Scroll to top when component mounts
    }, [dataLoaded]);

    // After new insight added
    const handleCloseAddTracker = async (tracker) => {
        if (tracker === null) setNewCampDiag(false);
        else {
            Utils.sendProgressBar(true);
            await getCompanyData();
            setNewCampDiag(false);
        }
    }

    const addPhraseAPI = async (aid, bid, phraseText) => {
        const found = data[aid].phrases.find((text, i) => text.trim().toLowerCase() === phraseText.trim().toLowerCase() && i !== bid);
        if (found) {
            showSnackbar(`Phrase "${phraseText}" already exists.`, "error");
            return;
        }
        try {
            setChangeIndex(aid + "-" + bid)
            const dataCopy = Object.assign([], data);
            dataCopy[aid].phrases[bid] = phraseText;
            dataCopy[aid].phrases = dataCopy[aid].phrases.filter(z => z.trim())
            setData(dataCopy);
            // toggle data edit
            const _dataToEdit = Object.assign([], dataToEdit);
            _dataToEdit[aid].phrases[bid] = !_dataToEdit[aid].phrases[bid];
            setDataToEdit(_dataToEdit);

            // update the company generic
            await Utils.genericMutation("updateCompany", {
                id: companyDT.id,
                trackers: JSON.stringify(dataCopy),
            });
            // getCompanyData();
            showSnackbar(PHRASE_ADDED_SUCCESS);
            setChangeIndex(null)
        } catch (error) {
            setChangeIndex(null)
            showSnackbar(PHRASE_ADDED_FAILED, "error");
        }
    }

    const handleChange = (panel) => (event, newExpanded) => {
        setExpanded(newExpanded ? panel : false);
    };


    useEffect(() => {
        if (!isNaN(expanded)) {
            scrollToElement();
        }
    }, [expanded])


    return (
        <Grid display="flex" flexDirection="column" flexGrow="1" overflow="auto">
            <SettingHeader title={"Insights"} selectedTab={selectedTab} />
            <Stack
                direction="row"
                alignItems="center"
                gap={1}
                flexWrap="wrap"
                justifyContent="end"
                paddingX={3}
                paddingTop={2}
            >
                <Button
                    variant="contained"
                    onClick={() => setNewCampDiag(true)}
                    startIcon={<FeatherIcon icon="plus-circle" size={20} />}
                    disabled={spinner || true} // VOC-199 DISABLED FOR DEMO
                >
                    Create New Insight
                </Button>
            </Stack>
            <Grid
                ref={scrollContainer}
                className="vocalysed-home-upadted setting-insight-admin"
                container
                spacing={0}
                direction="column"
                flexWrap="nowrap"
                flexGrow={1}
                paddingTop={2}
                paddingX={3}
                paddingBottom={3}
                overflow="auto"
            >
                <Paper
                    sx={{ display: "flex", flexDirection: "column", flexGrow: 1, width: '100%', overflow: 'auto', borderRadius: 2.5, py: 3 }}
                >
                    <Box overflow="auto" sx={{px: 3}}>
                        {spinner ? (
                            <Accordion>
                                <AccordionSummary
                                    aria-controls="panel1a-content"
                                    id="panel1a-header"
                                >
                                    <div
                                        style={{
                                            fontWeight: "bold",
                                            textAlign: "center",
                                            width: "100%",
                                        }}
                                    >
                                        <CircularProgress style={{ color: colours.primary100 }} />
                                    </div>
                                </AccordionSummary>

                                <AccordionDetails>
                                    <Table>
                                        <TableHead>
                                            <TableRow />
                                        </TableHead>
                                        <TableBody>
                                            <TableRow></TableRow>
                                        </TableBody>
                                    </Table>
                                </AccordionDetails>
                            </Accordion>
                        ) : (
                            data.map((a, aid) => (
                                <Accordion key={aid} expanded={expanded === aid} onChange={handleChange(aid)}>
                                    <AccordionSummary
                                        expandIcon={<FeatherIcon icon="chevron-down" size={24} />}
                                        aria-controls={`panel${aid + 1}a-content`}
                                        id={`panel${aid + 1}a-header`}
                                    >
                                        <Typography variant="h6" component="div" sx={{ fontWeight: 500, mr: 'auto', paddingRight: 2, }}>
                                            {a.name}
                                        </Typography>
                                        <CustomTooltip title="Add New Phrase">
                                            <IconButton
                                                aria-label="Add New Phrase"
                                                color="primary"
                                                size="small"
                                                onClick={async (e) => {
                                                    if (expanded === aid) {
                                                        e.stopPropagation(); // Prevent closing if already expanded
                                                    }
                                                    const _data = Object.assign([], data);
                                                    _data[aid].phrases = _data[aid].phrases.filter(z => z.trim())

                                                    const _dataToEdit = Object.assign([], dataToEdit);
                                                    _dataToEdit[aid].phrases = _data[aid].phrases.map(s => false);

                                                    _data[aid].phrases.push("");
                                                    _dataToEdit[aid].phrases.push(true);

                                                    setDataToEdit(_dataToEdit);
                                                    setData(_data);
                                                    // remove the API call for blank
                                                    // await Utils.genericMutation("updateCompany", {
                                                    //     id: companyDT.id,
                                                    //     trackers: JSON.stringify(_data),
                                                    // });
                                                    // await getCompanyData();
                                                }}
                                                // startIcon={<FeatherIcon icon="plus-circle" size={20} />}
                                                disabled={true} // VOC-199 DISABLED FOR DEMO
                                                sx={{ marginRight: 1 }}
                                            >
                                                <FeatherIcon icon="plus-circle" size={24} />
                                            </IconButton>
                                        </CustomTooltip>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {expanded === aid ? (
                                            <Paper
                                                sx={{ display: "flex", flexDirection: "column", flexGrow: 1, width: '100%', overflow: 'hidden', borderRadius: 2.5 }}
                                            >
                                                <TableContainer sx={{ maxHeight: 300, flexGrow: 1 }}>
                                                    <Table stickyHeader aria-label="sticky table">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell
                                                                    colSpan={2}
                                                                    sx={{
                                                                        fontWeight: 500,
                                                                    }}
                                                                >
                                                                    Phrases
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {a.phrases.map((b, bid) => (
                                                                <TableRow hover key={bid}>
                                                                    {!dataToEdit[aid].phrases[bid] ? (
                                                                        <React.Fragment>
                                                                            <TableCell>{b}</TableCell>
                                                                            <TableCell width={128}>
                                                                                <Stack direction="row" alignItems="center" justifyContent="end">
                                                                                    <CustomTooltip title={"Edit"}>
                                                                                        <IconButton
                                                                                            aria-label="Edit"
                                                                                            size="small"
                                                                                            color="inherit"
                                                                                            disabled={true} // VOC-199 DISABLED FOR DEMO
                                                                                            onClick={async () => {
                                                                                                const _dataToEdit = Object.assign([], dataToEdit);
                                                                                                _dataToEdit[aid].phrases[bid] = !_dataToEdit[aid].phrases[bid];

                                                                                                // close other edit modes, keep only one in edit mode
                                                                                                _dataToEdit[aid].phrases.forEach((element, index) => {
                                                                                                    if (bid !== index) {
                                                                                                        _dataToEdit[aid].phrases[index] = !_dataToEdit[aid].phrases[bid];
                                                                                                    }
                                                                                                });

                                                                                                const _data = Object.assign([], data);
                                                                                                _data[aid].phrases = _data[aid].phrases.filter(z => z.trim());
                                                                                                setData(_data);

                                                                                                setDataToEdit(_dataToEdit);
                                                                                            }}
                                                                                        >
                                                                                            {changeIndex === (aid + "-" + bid) ? (<CircularProgress size={20} />) : <FeatherIcon icon="edit-2" size={20} />}
                                                                                        </IconButton>
                                                                                    </CustomTooltip>
                                                                                    <DeleteConfirmButton
                                                                                        id={companyDT.id}
                                                                                        trackerIndex={aid}
                                                                                        phraseIndex={bid}
                                                                                        data={data}
                                                                                        onSuccess={(_data) => {
                                                                                            setData(_data);
                                                                                        }}
                                                                                    />
                                                                                </Stack>
                                                                            </TableCell>
                                                                        </React.Fragment>
                                                                    ) : (
                                                                        <TableCell colSpan={2}>
                                                                            <AddPhrase
                                                                                key={aid + "-" + bid}
                                                                                value={data[aid].phrases[bid]}
                                                                                onSave={async (phraseText) => addPhraseAPI(aid, bid, phraseText)}
                                                                            />
                                                                        </TableCell>
                                                                    )}
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Paper>
                                        ) : ""}
                                    </AccordionDetails>
                                </Accordion>
                            ))
                        )}
                    </Box>
                </Paper>
            </Grid>

            {/* Add Tracker Modal */}
            <AddTracker
                id={companyDT.id}
                trackers={data}
                open={newCampDiag}
                onClose={handleCloseAddTracker}
            />
        </Grid>
    );
}

export default TrackerAdmin;